import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Alert, ProgressBar } from 'react-bootstrap';
import { BsCloudArrowUp } from 'react-icons/bs';
import { FiFile, FiX } from 'react-icons/fi';
import instance from "../../../services/baseService";

const ManualLetterUpload = ({ show, onHide, clientId,reportId,port_code_id, reFetchcallback }) => {
    const [file, setFile] = useState(null);
    const [type, setType] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDragOver, setIsDragOver] = useState(false);
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState('');
    const fileInputRef = useRef(null);

    const isValid = file && type;

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const handleFileSelect = (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            
            // Validate file type
            if (!selectedFile.type.includes('pdf') && !selectedFile.name.toLowerCase().endsWith('.pdf')) {
                setErrors({ file: 'Please select a valid PDF file' });
                return;
            }

            // Validate file size (10MB limit)
            if (selectedFile.size > 10 * 1024 * 1024) {
                setErrors({ file: 'File size must be less than 10MB' });
                return;
            }

            setFile(selectedFile);
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.file;
                return newErrors;
            });
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        handleFileSelect(e.dataTransfer.files);
    };

    const removeFile = () => {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const resetForm = () => {
        setFile(null);
        setType('');
        setErrors({});
        setSuccess('');
        setUploadProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClose = () => {
        if (!isUploading) {
            resetForm();
            onHide(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Clear previous errors
        setErrors({});
        setSuccess('');

        // Validation
        const newErrors = {};

        if (!file) {
            newErrors.file = 'Please select a PDF file to upload';
        }

        if (!type) {
            newErrors.type = 'Please select a type (Inward/Outward)';
        }

        if (!clientId) {
            newErrors.submit = 'Client ID is required for upload';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            // Create FormData for API request
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', type);

            // Start progress simulation
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev < 90) {
                        return prev + 10;
                    }
                    return prev;
                });
            }, 200);

            // Make API call
            const response = await instance.post(
                `/api/v1/reconciliation/manual/transfer/letter/upload/${clientId}/${reportId}/${port_code_id}`, // Adjust endpoint as needed
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            // Clear progress interval
            clearInterval(progressInterval);
            setUploadProgress(100);

            // Handle response based on status
            if (response.data && response.data.type === 'success') {
                const { data } = response.data;
                setSuccess('File uploaded successfully!');
                
                await new Promise(resolve => setTimeout(resolve, 2500));
                
                if (reFetchcallback) {
                    await reFetchcallback();
                }
                
                resetForm();
                onHide(true, data);
            } else {
                throw new Error(response.data?.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            setErrors({
                submit: error.response?.data?.message || error.message || 'Failed to upload file. Please try again.'
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            centered
            backdrop="static"
        >
            <Modal.Header closeButton className="border-0 d-flex justify-content-between align-items-start">
                <div className="flex-grow-1 text-start">
                    <Modal.Title className="fw-bold">Upload Transfer Letter</Modal.Title>
                    <p className="text-muted mb-0">Upload PDF file for processing.</p>
                </div>
            </Modal.Header>
            <Modal.Body className="p-3">
                <Form onSubmit={handleSubmit}>
                    {/* Type Selection */}
                    <div className="mb-4">
                        <h5 className="fw-semibold mb-3">Select Type</h5>
                        <Form.Group>
                            <Form.Select
                                value={type}
                                onChange={(e) => {
                                    setType(e.target.value);
                                    // Clear Type error when user selects
                                    if (errors.type) {
                                        setErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.type;
                                            return newErrors;
                                        });
                                    }
                                }}
                                isInvalid={!!errors.type}
                                disabled={isUploading}
                            >
                                <option value="">Select type...</option>
                                <option value="inward">Inward</option>
                                <option value="outward">Outward</option>
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                                {errors.type}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </div>

                    {/* Upload Section */}
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-semibold mb-0">Upload PDF File</h5>
                        </div>
                        <div
                            className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => !isUploading && fileInputRef.current?.click()}
                            style={{
                                border: '2px dashed #dee2e6',
                                borderRadius: '8px',
                                padding: '40px 20px',
                                textAlign: 'center',
                                cursor: isUploading ? 'not-allowed' : 'pointer',
                                backgroundColor: isDragOver ? '#f8f9fa' : 'transparent',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <div className="upload-icon" style={{ fontSize: '48px', color: '#0d6efd', marginBottom: '10px' }}>
                                <BsCloudArrowUp />
                            </div>
                            <h6 className="mb-2">
                                <span className="text-primary text-decoration-none">Upload file</span>
                                <span className="text-muted"> or drag and drop</span>
                            </h6>
                            <small className="text-muted">PDF up to 10MB</small>
                            <Form.Control
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={(e) => {
                                    handleFileSelect(e.target.files);
                                    // reset input so selecting the same file again triggers onChange
                                    e.target.value = "";
                                }}
                                className="d-none"
                                disabled={isUploading}
                            />
                        </div>

                        {errors.file && (
                            <Alert 
                                variant="danger" 
                                className="mt-2" 
                                dismissible
                                onClose={() => setErrors(prev => ({ ...prev, file: null }))}
                            >
                                {errors.file}
                            </Alert>
                        )}
                    </div>

                    {/* Selected File Display */}
                    {file && (
                        <div className="mb-4">
                            <h6 className="fw-semibold mb-3">Selected File</h6>
                            <div className="selected-files">
                                <div 
                                    className="file-item" 
                                    style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '8px',
                                        padding: '12px'
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <FiFile className="text-primary me-2" size={24} />
                                        <div className="flex-grow-1">
                                            <div className="file-name fw-medium">{file.name}</div>
                                            <small className="text-muted">
                                                {formatFileSize(file.size)}
                                            </small>
                                        </div>
                                        {!isUploading && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-danger p-1"
                                                onClick={removeFile}
                                                title="Remove file"
                                            >
                                                <FiX size={20} />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Success Message */}
                    {success && (
                        <Alert variant="success" className="py-2 mb-3">
                            {success}
                        </Alert>
                    )}

                    {/* Error Messages */}
                    {errors.submit && (
                        <Alert variant="danger" className="py-2 mb-3">
                            {errors.submit}
                        </Alert>
                    )}

                    {/* Progress Section */}
                    {isUploading && (
                        <div className="progress-section mb-4">
                            <h6 className="fw-semibold mb-3">Upload Progress</h6>
                            <ProgressBar
                                now={uploadProgress}
                                className="custom-progress mb-2"
                            />
                            <small className="text-muted">{uploadProgress}% complete</small>
                        </div>
                    )}
                </Form>
            </Modal.Body>

            <Modal.Footer className='border-0'>
                <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isUploading}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isUploading || !isValid}
                    className="d-flex align-items-center"
                >
                    {isUploading ? (
                        <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            Uploading...
                        </>
                    ) : (
                        'Upload'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ManualLetterUpload;